import React, { useMemo, useState } from 'react'

interface CustomDateRangePickerProps {
  onApply: (startUtc: string, endUtc: string) => void
  onCancel: () => void
  initialStart?: string
  initialEnd?: string
}

const MS_90_DAYS = 90 * 24 * 60 * 60 * 1000

// Convert a UTC ISO-8601 string to the value format expected by datetime-local inputs
const toDateTimeLocalValue = (iso: string): string => {
  const d = new Date(iso)
  // Format: YYYY-MM-DDTHH:MM (no seconds, no timezone — datetime-local is always local)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

// Format datetime-local value as the max attribute (current local time, no seconds)
const nowLocalValue = (): string =>
  toDateTimeLocalValue(new Date().toISOString())

// @purpose Dialog for picking an absolute start/end datetime range in local time, validating order/future bounds and emitting UTC ISO strings
const CustomDateRangePicker = ({
  onApply,
  onCancel,
  initialStart,
  initialEnd,
}: CustomDateRangePickerProps): React.JSX.Element => {
  const [startLocal, setStartLocal] = useState(
    initialStart ? toDateTimeLocalValue(initialStart) : '',
  )
  const [endLocal, setEndLocal] = useState(
    initialEnd ? toDateTimeLocalValue(initialEnd) : '',
  )
  const [error, setError] = useState<string | null>(null)

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Non-blocking advisory: shown when both fields are filled and range exceeds 90 days
  const largeRangeAdvisory = useMemo(() => {
    if (!startLocal || !endLocal) return null
    const start = new Date(startLocal)
    const end = new Date(endLocal)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null
    return end.getTime() - start.getTime() > MS_90_DAYS
      ? 'Large time ranges may take longer to load'
      : null
  }, [startLocal, endLocal])

  const handleApply = () => {
    if (!startLocal || !endLocal) {
      setError('Both start and end are required')
      return
    }
    const startUtc = new Date(startLocal).toISOString()
    const endUtc = new Date(endLocal).toISOString()
    if (new Date(endUtc) <= new Date(startUtc)) {
      setError('Start time must be before end time')
      return
    }
    if (new Date(endUtc) > new Date()) {
      setError('End time cannot be in the future')
      return
    }
    setError(null)
    onApply(startUtc, endUtc)
  }

  return (
    <div
      role="dialog"
      aria-label="Custom absolute date range"
      className="p-4 space-y-3 min-w-[280px]"
    >
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Times are in your local timezone ({tz})
      </p>

      <div className="space-y-2">
        <label className="block">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
            Start
          </span>
          <input
            type="datetime-local"
            value={startLocal}
            max={nowLocalValue()}
            onChange={e => {
              setStartLocal(e.target.value)
              setError(null)
            }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:light] dark:[color-scheme:dark]"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
            End
          </span>
          <input
            type="datetime-local"
            value={endLocal}
            min={startLocal || undefined}
            max={nowLocalValue()}
            onChange={e => {
              setEndLocal(e.target.value)
              setError(null)
            }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:light] dark:[color-scheme:dark]"
          />
        </label>
      </div>

      {error && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {!error && largeRangeAdvisory && (
        <p role="note" className="text-xs text-amber-600 dark:text-amber-400">
          ⚠ {largeRangeAdvisory}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleApply}
          className="flex-1 px-3 py-1.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
        >
          Apply
        </button>
        <button
          onClick={onCancel}
          className="flex-1 px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default CustomDateRangePicker
